import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import { AnswerFilter } from '../../api/@types';
import { MultiSelectClassificationState } from '../../components/MultiSelectClassification';
import { selectOptionDefault } from '../../components/SelectClassification';
import { MultiSelectOption, SelectOption } from '../../typings/select-option.typings';

export type ContainsComments = 'WITH_COMMENTS' | 'WITH_MY_COMMENTS' | null;

export interface YearGap {
  minYear: number;
  maxYear: number;
}

export interface ActivityCreateSingleClassificationsState {
  areaSingle: SelectOption;
  expertiseSingle: SelectOption;
  topicSingle: SelectOption;
  subtopicSingle: SelectOption;
}

export interface ActivityCreatePageState
  extends MultiSelectClassificationState, ActivityCreateSingleClassificationsState {
  type?: string;
  region: MultiSelectOption;
  institution: MultiSelectOption;
  minYear: number;
  maxYear: number;
  loading: boolean;
  length: string;
  difficulty: MultiSelectOption;
  containsImage: boolean;
  statusComments: boolean;
  positiveRating: boolean;
  answer: AnswerFilter | '';
}

export interface ActivityCreatePageSetStatePayload {
  name: keyof ActivityCreatePageState;
  value: string | string[] | boolean;
}

export const createActivityInitialState: ActivityCreatePageState = {
  type: 'question',
  area: [],
  expertise: [],
  topic: [],
  subtopic: [],
  areaSingle: selectOptionDefault,
  expertiseSingle: selectOptionDefault,
  topicSingle: selectOptionDefault,
  subtopicSingle: selectOptionDefault,
  region: [],
  institution: [],
  minYear: 0,
  maxYear: 0,
  length: '10',
  difficulty: [],
  containsImage: false,
  statusComments: false,
  positiveRating: false,
  answer: '',

  // Não persistentes
  loading: false,
};

const { reducer, actions } = createSlice({
  name: 'activityCreatePage',
  initialState: createActivityInitialState,
  reducers: {
    reset: (
      state,
      action: PayloadAction<YearGap>,
    ): ActivityCreatePageState => {
      return {
        ...state,
        type: 'question',
        area: [],
        expertise: [],
        topic: [],
        subtopic: [],
        areaSingle: selectOptionDefault,
        expertiseSingle: selectOptionDefault,
        topicSingle: selectOptionDefault,
        subtopicSingle: selectOptionDefault,
        region: [],
        institution: [],
        minYear: action.payload.minYear,
        maxYear: action.payload.maxYear,
        length: '10',
        answer: '',
        difficulty: [],
        containsImage: false,
        statusComments: false,
        positiveRating: false,
      };
    },
    setState: (state, action: PayloadAction<ActivityCreatePageSetStatePayload>): ActivityCreatePageState => {
      const { name, value } = action.payload;
      if (state[name] === value) {
        return state;
      }
      return { ...state, [name]: value };
    },
    setInstitution: (
      state,
      action: PayloadAction<MultiSelectOption>,
    ): ActivityCreatePageState => {
      return {
        ...state,
        institution: action.payload,
      };
    },
    setYearRange: (state, action: PayloadAction<YearGap>): ActivityCreatePageState => {
      const { maxYear, minYear } = action.payload;
      return { ...state, minYear, maxYear };
    },
    setSingleClassifications: (
      state,
      action: PayloadAction<{ classifications: ActivityCreateSingleClassificationsState }>,
    ): ActivityCreatePageState => {
      const { classifications } = action.payload;
      return { ...state, ...classifications };
    },
    setClassifications: (
      state,
      action: PayloadAction<{ classifications: MultiSelectClassificationState }>,
    ): ActivityCreatePageState => {
      const { classifications } = action.payload;
      return { ...state, ...classifications };
    },
    toggleBoolean: (
      state,
      action: PayloadAction<'containsImage' | 'positiveRating'>,
    ): ActivityCreatePageState => {
      const key = action.payload;
      return { ...state, [key]: !state[key] };
    },
    setLoading: (
      state,
      action: PayloadAction<boolean>,
    ): ActivityCreatePageState => {
      const loading = action.payload;
      return { ...state, loading };
    },
  },
});

const persistentReducer = persistReducer({
  key: 'activityCreatePage',
  storage,
  blacklist: [
    'loading',
  ],
}, reducer);

export { actions };
export default persistentReducer;
